import { Util } from '../../global';
import { ObjectExt, StringExt } from '../../util';
import { Rectangle, Angle } from '../../geometry';
import { Cell } from '../../model/cell';
import { Collection } from '../../model/collection';
import { View } from '../../view/view';
import { Handle } from '../common';
export class Selection extends View {
    constructor(options) {
        super();
        this.options = ObjectExt.merge({}, Private.defaultOptions, options);
        if (this.options.model) {
            this.options.collection = this.options.model.collection;
        }
        if (this.options.collection) {
            this.collection = this.options.collection;
        }
        else {
            this.collection = new Collection([], {
                comparator: Private.depthComparator,
            });
            this.options.collection = this.collection;
        }
        this.boxCount = 0;
        this.createContainer();
        this.initHandles();
        this.startListening();
    }
    get graph() {
        return this.options.graph;
    }
    get boxClassName() {
        return this.prefixClassName(Private.classNames.box);
    }
    get $boxes() {
        return this.$container.children(`.${this.boxClassName}`);
    }
    get handleOptions() {
        return this.options;
    }
    startListening() {
        const graph = this.graph;
        const collection = this.collection;
        this.delegateEvents({
            [`mousedown .${this.boxClassName}`]: 'onSelectionBoxMouseDown',
            [`touchstart .${this.boxClassName}`]: 'onSelectionBoxMouseDown',
        }, true);
        graph.on('scale', this.onTransformed, this);
        graph.on('translate', this.onTransformed, this);
        collection.on('added', this.onCellAdded, this);
        collection.on('removed', this.onCellRemoved, this);
        collection.on('reseted', this.onReseted, this);
        collection.on('updated', this.onCollectionUpdated, this);
        collection.on('cell:change:*', this.onCellChanged, this);
    }
    stopListening() {
        const graph = this.graph;
        const collection = this.collection;
        this.undelegateEvents();
        graph.off('scale', this.onTransformed, this);
        graph.off('translate', this.onTransformed, this);
        collection.off('removed', this.onCellRemoved, this);
        collection.off('reseted', this.onReseted, this);
        collection.off('added', this.onCellAdded, this);
        collection.off('cell:change:*', this.onCellChanged, this);
    }
    onRemove() {
        this.stopListening();
    }
    onTransformed() {
        this.updateSelectionBoxes({ async: false });
    }
    onCellChanged() {
        this.updateSelectionBoxes();
    }
    isEmpty() {
        return this.length <= 0;
    }
    isSelected(cell) {
        return this.collection.has(cell);
    }
    get length() {
        return this.collection.length;
    }
    get cells() {
        return this.collection.toArray();
    }
    select(cells, options = {}) {
        this.collection.add(cells, options);
        return this;
    }
    unselect(cells, options = {}) {
        // dryrun to prevent cell be removed from graph
        options.dryrun = true;
        this.collection.remove(Array.isArray(cells) ? cells : [cells], options);
        return this;
    }
    clean() {
        if (this.length) {
            this.collection.reset([], { ui: true });
        }
        return this;
    }
    setFilter(filter) {
        this.options.filter = filter;
    }
    setContent(content) {
        this.options.content = content;
    }
    startSelecting(evt) {
        // Flow: startSelecting => adjustSelection => stopSelecting
        evt = this.normalizeEvent(evt); // tslint:disable-line
        this.clean();
        let x;
        let y;
        const graphContainer = this.graph.container;
        if (null != evt.offsetX &&
            null != evt.offsetY &&
            graphContainer.contains(evt.target)) {
            x = evt.offsetX;
            y = evt.offsetY;
        }
        else {
            const offset = this.$(graphContainer).offset();
            const scrollLeft = graphContainer.scrollLeft;
            const scrollTop = graphContainer.scrollTop;
            x = evt.clientX - offset.left + window.pageXOffset + scrollLeft;
            y = evt.clientY - offset.top + window.pageYOffset + scrollTop;
        }
        this.$container
            .css({
            width: 1,
            height: 1,
            left: x,
            top: y,
        })
            .appendTo(this.graph.container);
        this.showRubberband();
        this.setEventData(evt, {
            action: 'selecting',
            clientX: evt.clientX,
            clientY: evt.clientY,
            offsetX: x,
            offsetY: y,
        });
        this.delegateDocumentEvents(Private.documentEvents, evt.data);
    }
    stopSelecting(evt) {
        const graph = this.graph;
        const action = this.getEventData(evt).action;
        switch (action) {
            case 'selecting': {
                let width = this.$container.width();
                let height = this.$container.height();
                const offset = this.$container.offset();
                const origin = graph.pageToLocalPoint(offset.left, offset.top);
                const scale = graph.scale();
                width = width / scale.sx;
                height = height / scale.sy;
                const rect = new Rectangle(origin.x, origin.y, width, height);
                let views = this.getNodesInArea(rect);
                const filter = this.options.filter;
                if (Array.isArray(filter)) {
                    views = views.filter((view) => !filter.includes(view.cell) && !filter.includes(view.cell.shape));
                }
                else {
                    if (typeof filter === 'function') {
                        views = views.filter((view) => !filter.call(this.graph, view.cell));
                    }
                }
                const cells = views.map((view) => view.cell);
                this.collection.reset(cells, { ui: true });
                break;
            }
            case 'translating': {
                this.graph.model.stopBatch('move-selection');
                const client = graph.snapToGrid(evt.clientX, evt.clientY);
                this.notifyBoxEvent('box:mouseup', evt, client.x, client.y);
                break;
            }
            default: {
                if (!action) {
                    this.clean();
                }
            }
        }
    }
    onMouseUp(evt) {
        const action = this.getEventData(evt).action;
        if (action) {
            this.stopSelecting(evt);
            this.undelegateDocumentEvents();
        }
    }
    onSelectionBoxMouseDown(evt) {
        evt.stopPropagation();
        const e = this.normalizeEvent(evt);
        if (this.options.movable) {
            this.startTranslating(e);
        }
        const activeView = this.getCellViewFromElem(e.target);
        this.setEventData(e, { activeView });
        const client = this.graph.snapToGrid(e.clientX, e.clientY);
        this.notifyBoxEvent('box:mousedown', e, client.x, client.y);
        this.delegateDocumentEvents(Private.documentEvents, e.data);
    }
    startTranslating(evt) {
        this.graph.model.startBatch('move-selection');
        const client = this.graph.snapToGrid(evt.clientX, evt.clientY);
        this.setEventData(evt, {
            action: 'translating',
            clientX: client.x,
            clientY: client.y,
        });
    }
    adjustSelection(evt) {
        const e = this.normalizeEvent(evt);
        const eventData = this.getEventData(e);
        const action = eventData.action;
        switch (action) {
            case 'selecting': {
                const data = eventData;
                const dx = e.clientX - data.clientX;
                const dy = e.clientY - data.clientY;
                const left = parseInt(this.$container.css('left'), 10);
                const top = parseInt(this.$container.css('top'), 10);
                this.$container.css({
                    left: dx < 0 ? data.offsetX + dx : left,
                    top: dy < 0 ? data.offsetY + dy : top,
                    width: Math.abs(dx),
                    height: Math.abs(dy),
                });
                break;
            }
            case 'translating': {
                const data = eventData;
                const client = this.graph.snapToGrid(e.clientX, e.clientY);
                let dx = client.x - data.clientX;
                let dy = client.y - data.clientY;
                const restrictedArea = this.graph.getRestrictedArea();
                if (restrictedArea) {
                    const cells = this.collection.toArray();
                    const totalBBox = Cell.getCellsBBox(cells);
                    const minDx = restrictedArea.x - totalBBox.x;
                    const minDy = restrictedArea.y - totalBBox.y;
                    const maxDx = restrictedArea.x +
                        restrictedArea.width -
                        (totalBBox.x + totalBBox.width);
                    const maxDy = restrictedArea.y +
                        restrictedArea.height -
                        (totalBBox.y + totalBBox.height);
                    if (dx < minDx) {
                        dx = minDx;
                    }
                    if (dy < minDy) {
                        dy = minDy;
                    }
                    if (maxDx < dx) {
                        dx = maxDx;
                    }
                    if (maxDy < dy) {
                        dy = maxDy;
                    }
                }
                if (dx || dy) {
                    if ((this.translateSelectedNodes(dx, dy), this.boxesUpdated)) {
                        if (this.collection.length > 1) {
                            this.updateSelectionBoxes();
                        }
                    }
                    else {
                        const scale = this.graph.scale();
                        this.$boxes.add(this.$selectionContainer).css({
                            left: `+=${dx * scale.sx}`,
                            top: `+=${dy * scale.sy}`,
                        });
                    }
                    data.clientX = client.x;
                    data.clientY = client.y;
                }
                this.notifyBoxEvent('box:mousemove', evt, client.x, client.y);
                break;
            }
        }
        this.boxesUpdated = false;
    }
    translateSelectedNodes(dx, dy) {
        const map = {};
        this.collection.toArray().forEach((cell) => {
            if (!map[cell.id]) {
                const options = {
                    selection: this.cid,
                };
                cell.translate(dx, dy, options);
                cell.getDescendants({ deep: true }).forEach((child) => {
                    map[child.id] = true;
                });
                this.graph.model.getConnectedEdges(cell).forEach((edge) => {
                    if (!map[edge.id]) {
                        edge.translate(dx, dy, options);
                        map[edge.id] = true;
                    }
                });
            }
        });
    }
    getNodesInArea(rect) {
        const graph = this.graph;
        const options = {
            strict: this.options.strict,
        };
        return this.options.useCellBBox
            ? graph.model
                .getNodesInArea(rect, options)
                .map((node) => graph.renderer.findViewByCell(node))
                .filter((view) => view != null)
            : graph.renderer.findViewsInArea(rect, options);
    }
    notifyBoxEvent(name, e, x, y) {
        const data = this.getEventData(e);
        const view = data.activeView;
        this.trigger(name, { e, view, x, y, cell: view.cell });
    }
    destroySelectionBox(cell) {
        this.$container.find(`[data-cell="${cell.id}"]`).remove();
        if (this.$boxes.length === 0) {
            this.hide();
        }
        this.boxCount = Math.max(0, this.boxCount - 1);
    }
    destroyAllSelectionBoxes() {
        this.hide();
        this.$boxes.remove();
        this.boxCount = 0;
    }
    hide() {
        this.$container
            .removeClass(this.prefixClassName(Private.classNames.rubberband))
            .removeClass(this.prefixClassName(Private.classNames.selected));
    }
    showRubberband() {
        this.$container.addClass(this.prefixClassName(Private.classNames.rubberband));
    }
    showSelected() {
        this.$container.addClass(this.prefixClassName(Private.classNames.selected));
    }
    createContainer() {
        this.container = document.createElement('div');
        this.$container = this.$(this.container);
        this.$container.addClass(this.prefixClassName(Private.classNames.root));
        if (this.options.className) {
            this.$container.addClass(this.options.className);
        }
        this.$selectionContainer = this.$('<div/>').addClass(this.prefixClassName(Private.classNames.inner));
        this.$selectionContent = this.$('<div/>').addClass(this.prefixClassName(Private.classNames.content));
        this.$selectionContainer.append(this.$selectionContent);
        this.$selectionContainer.attr('data-selection-length', this.collection.length);
        this.$container.prepend(this.$selectionContainer);
        this.$handleContainer = this.$selectionContainer;
    }
    updateContainer() {
        const origin = { x: Infinity, y: Infinity };
        const corner = { x: 0, y: 0 };
        this.collection.toArray().forEach((cell) => {
            const view = this.graph.renderer.findViewByCell(cell);
            if (view) {
                const bbox = view.getBBox({
                    useCellBBox: this.options.useCellBBox,
                });
                origin.x = Math.min(origin.x, bbox.x);
                origin.y = Math.min(origin.y, bbox.y);
                corner.x = Math.max(corner.x, bbox.x + bbox.width);
                corner.y = Math.max(corner.y, bbox.y + bbox.height);
            }
        });
        this.$selectionContainer
            .css({
            left: origin.x,
            top: origin.y,
            width: corner.x - origin.x,
            height: corner.y - origin.y,
        })
            .attr('data-selection-length', this.collection.length);
        const boxContent = this.options.content;
        if (boxContent) {
            if (typeof boxContent === 'function') {
                const content = boxContent.call(this.graph, this, this.$selectionContent[0]);
                if (content) {
                    this.$selectionContent.html(content);
                }
            }
            else {
                this.$selectionContent.html(boxContent);
            }
        }
        if (this.collection.length > 0 && !this.container.parentNode) {
            this.$container.appendTo(this.graph.container);
        }
        else if (this.collection.length <= 0 && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
    createSelectionBox(cell) {
        const view = this.graph.renderer.findViewByCell(cell);
        if (view) {
            const bbox = view.getBBox({
                useCellBBox: this.options.useCellBBox,
            });
            this.$('<div/>')
                .addClass(this.boxClassName)
                .attr('data-cell', cell.id)
                .css({
                left: bbox.x,
                top: bbox.y,
                width: bbox.width,
                height: bbox.height,
            })
                .appendTo(this.container);
            this.showSelected();
            this.boxCount += 1;
        }
    }
    updateSelectionBoxes(options = {}) {
        if (this.collection.length > 0) {
            this.boxesUpdated = true;
            this.graph.renderer.requestViewUpdate(this, 1, 2, options);
        }
    }
    confirmUpdate() {
        if (this.boxCount) {
            this.hide();
            this.$boxes.each((_, elem) => {
                const cellId = this.$(elem).remove().attr('data-cell');
                const cell = this.collection.get(cellId);
                if (cell) {
                    this.createSelectionBox(cell);
                }
            });
            this.updateContainer();
        }
        return 0;
    }
    getCellViewFromElem(elem) {
        const id = elem.getAttribute('data-cell');
        if (id) {
            const cell = this.collection.get(id);
            if (cell) {
                return this.graph.renderer.findViewByCell(cell);
            }
        }
        return null;
    }
    onCellRemoved({ cell }) {
        this.destroySelectionBox(cell);
        this.updateContainer();
    }
    onReseted({ current }) {
        this.destroyAllSelectionBoxes();
        current.forEach((cell) => this.createSelectionBox(cell));
        this.updateContainer();
    }
    onCellAdded({ cell }) {
        this.createSelectionBox(cell);
        this.updateContainer();
    }
    onCollectionUpdated({ added, removed, options, }) {
        added.forEach((cell) => {
            this.trigger('cell:selected', { cell, options });
            this.graph.trigger('cell:selected', { cell, options });
            if (cell.isNode()) {
                this.trigger('node:selected', { cell, options, node: cell });
                this.graph.trigger('node:selected', { cell, options, node: cell });
            }
            else if (cell.isEdge()) {
                this.trigger('edge:selected', { cell, options, edge: cell });
                this.graph.trigger('edge:selected', { cell, options, edge: cell });
            }
        });
        removed.forEach((cell) => {
            this.trigger('cell:unselected', { cell, options });
            this.graph.trigger('cell:unselected', { cell, options });
            if (cell.isNode()) {
                this.trigger('node:unselected', { cell, options, node: cell });
                this.graph.trigger('node:unselected', { cell, options, node: cell });
            }
            else if (cell.isEdge()) {
                this.trigger('edge:unselected', { cell, options, edge: cell });
                this.graph.trigger('edge:unselected', { cell, options, edge: cell });
            }
        });
        const args = {
            added,
            removed,
            options,
            selected: this.cells,
        };
        this.trigger('selection:changed', args);
        this.graph.trigger('selection:changed', args);
    }
    // #region handle
    deleteSelectedCells() {
        const cells = this.collection.toArray();
        this.clean();
        this.graph.model.removeCells(cells, {
            selection: this.cid,
        });
    }
    startRotate({ e }) {
        const cells = this.collection.toArray();
        const center = Cell.getCellsBBox(cells).getCenter();
        const client = this.graph.snapToGrid(e.clientX, e.clientY);
        const angles = cells.reduce((memo, cell) => {
            memo[cell.id] = Angle.normalize(cell.getAngle());
            return memo;
        }, {});
        this.setEventData(e, {
            center,
            angles,
            start: client.theta(center),
        });
    }
    doRotate({ e }) {
        const data = this.getEventData(e);
        const grid = this.graph.options.rotating.grid;
        const gridSize = typeof grid === 'function' ? grid.call(this.graph) : grid;
        const client = this.graph.snapToGrid(e.clientX, e.clientY);
        const delta = data.start - client.theta(data.center);
        if (Math.abs(delta) > 0.001) {
            this.collection.toArray().forEach((node) => {
                const angle = Util.snapToGrid(data.angles[node.id] + delta, gridSize || 15);
                node.rotate(angle, {
                    absolute: true,
                    center: data.center,
                    selection: this.cid,
                });
            });
            this.updateSelectionBoxes();
        }
    }
    stopRotate() { }
    startResize({ e }) {
        const gridSize = this.graph.getGridSize();
        const cells = this.collection.toArray();
        const bbox = Cell.getCellsBBox(cells);
        const bboxes = cells.map((cell) => cell.getBBox());
        const maxWidth = bboxes.reduce((maxWidth, bbox) => {
            return bbox.width < maxWidth ? bbox.width : maxWidth;
        }, Infinity);
        const maxHeight = bboxes.reduce((maxHeight, bbox) => {
            return bbox.height < maxHeight ? bbox.height : maxHeight;
        }, Infinity);
        this.setEventData(e, {
            bbox,
            cells: this.graph.model.getSubGraph(cells),
            minWidth: (gridSize * bbox.width) / maxWidth,
            minHeight: (gridSize * bbox.height) / maxHeight,
        });
    }
    doResize({ e, dx, dy }) {
        const data = this.eventData(e);
        const bbox = data.bbox;
        const width = bbox.width;
        const height = bbox.height;
        const newWidth = Math.max(width + dx, data.minWidth);
        const newHeight = Math.max(height + dy, data.minHeight);
        if (0.001 < Math.abs(width - newWidth) ||
            0.001 < Math.abs(height - newHeight)) {
            this.graph.model.resizeCells(newWidth, newHeight, data.cells, {
                selection: this.cid,
            });
            bbox.width = newWidth;
            bbox.height = newHeight;
            this.updateSelectionBoxes();
        }
    }
    stopResize() { }
}
ObjectExt.applyMixins(Selection, Handle);
// private
// -------
var Private;
(function (Private) {
    const base = 'widget-selection';
    Private.classNames = {
        root: base,
        inner: `${base}-inner`,
        box: `${base}-box`,
        content: `${base}-content`,
        rubberband: `${base}-rubberband`,
        selected: `${base}-selected`,
    };
    Private.documentEvents = {
        mousemove: 'adjustSelection',
        touchmove: 'adjustSelection',
        mouseup: 'onMouseUp',
        touchend: 'onMouseUp',
        touchcancel: 'onMouseUp',
    };
    Private.defaultOptions = {
        movable: true,
        strict: false,
        useCellBBox: false,
        content(selection) {
            return StringExt.template('<%= length %> node<%= length > 1 ? "s":"" %> selected.')({ length: selection.length });
        },
        handles: [
            {
                name: 'remove',
                position: 'nw',
                events: {
                    mousedown: 'deleteSelectedCells',
                },
            },
            {
                name: 'rotate',
                position: 'sw',
                events: {
                    mousedown: 'startRotate',
                    mousemove: 'doRotate',
                    mouseup: 'stopRotate',
                },
            },
            {
                name: 'resize',
                position: 'se',
                events: {
                    mousedown: 'startResize',
                    mousemove: 'doResize',
                    mouseup: 'stopResize',
                },
            },
        ],
    };
    function depthComparator(cell) {
        return cell.getAncestors().length;
    }
    Private.depthComparator = depthComparator;
})(Private || (Private = {}));
//# sourceMappingURL=index.js.map